import React from 'react';
import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import { useForm } from 'react-hook-form';
import { InternalRefetchQueryDescriptor, useMutation } from '@apollo/client';
import { CoherencePanelStyles } from '../../../app/common/styles/CommonStyleObjects';
import { IDormancyAssessmentRequest } from '../types';
import ActionButtons from '../../common/ActionButtons';
import DormancyAssessmentForm from './DormancyAssessmentForm';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import {
  UPDATE_DORMANCY_ASSESSMENT,
  CREATE_DORMANCY_ASSESSMENT,
} from '../../../utils/statApi/EntityTerminationApi';

interface IDormancyAssessmentPanelProps {
  closePanel: () => void;
  dormancyData?: IDormancyAssessmentRequest;
  dormancyAssessmentRequest?: IDormancyAssessmentRequest;
  refetchQueries: InternalRefetchQueryDescriptor[];
}

const DormancyAssessmentPanel = ({
  closePanel,
  dormancyData,
  dormancyAssessmentRequest,
  refetchQueries,
}: IDormancyAssessmentPanelProps): JSX.Element => {
  const title = dormancyAssessmentRequest?.id !== undefined ? 'Edit ' : 'Create New ';
  const dormancyAssessmentRequestForm = useForm();
  const { handleSubmit } = dormancyAssessmentRequestForm;

  const [updateDormancyAssessment, { loading: updateLoading, error: updateError }] = useMutation(
    UPDATE_DORMANCY_ASSESSMENT,
    {
      refetchQueries,
      onCompleted: () => {
        closePanel();
      },
    },
  );

  const [createDormancyAssessment, { loading: createLoading, error: createError }] = useMutation(
    CREATE_DORMANCY_ASSESSMENT,
    {
      refetchQueries,
      onCompleted: () => {
        closePanel();
      },
    },
  );

  const handleSaveClick = (formData: IDormancyAssessmentRequest): void => {
    const dormancyAssessmentVariables = {
      dormancyAssessmentInput: {
        id: dormancyAssessmentRequest.id,
        companyCode: dormancyAssessmentRequest.companyCode,
        hasEmployees: formData.hasEmployees,
        hasRevenue: formData.hasRevenue,
        hasOperatingExpenses: formData.hasOperatingExpenses,
        mercuryDocumentLink: formData.mercuryDocumentLink,
        comments: formData.comments,
      },
      companyCode: dormancyAssessmentRequest.companyCode,
    };

    if (dormancyAssessmentRequest?.id !== null && dormancyAssessmentRequest?.id !== undefined) {
      updateDormancyAssessment({
        variables: {
          ...dormancyAssessmentVariables,
        },
      });
    } else {
      createDormancyAssessment({
        variables: {
          ...dormancyAssessmentVariables,
        },
      });
    }
  };

  return (
    <CoherencePanel
      panelSize={CoherencePanelSize.medium}
      titleText={`${title} Dormancy Assessment`}
      isOpen
      onDismiss={closePanel}
      styles={CoherencePanelStyles}
      hasCloseButton
      onRenderFooter={(): JSX.Element => (
        <ActionButtons
          closePanel={closePanel}
          saveLabel="Save Dormancy Assessment"
          saveTitle="Save Dormancy Assessment"
          cancelLabel="Cancel"
          cancelTitle="Cancel"
          handleSubmit={handleSubmit(handleSaveClick)}
        />
      )}
    >
      <form>
        <LoadingErrorMessage
          loading={createLoading || updateLoading}
          error={createError || updateError}
          actionName="saving the Dormancy Assessment"
        />
        <DormancyAssessmentForm form={dormancyAssessmentRequestForm} dormancyData={dormancyData} />
      </form>
    </CoherencePanel>
  );
};

export default DormancyAssessmentPanel;
